import { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Loader2, Phone, Square, Play, Trash2, CheckCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCreateCall, useAnalyzeCall } from "@/hooks/useCalls";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export function AIVoiceCallLogger() {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcript, setTranscript] = useState<string>("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  
  const createCall = useCreateCall();
  const analyzeCall = useAnalyzeCall();

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      toast.success("Recording started");
    } catch (error) {
      toast.error("Could not access microphone. Please check permissions.");
      console.error("Microphone error:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      toast.success("Recording stopped");
    }
  };

  const togglePause = () => {
    if (!mediaRecorderRef.current) return;

    if (isPaused) {
      mediaRecorderRef.current.resume();
      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      mediaRecorderRef.current.pause();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    setIsPaused(!isPaused);
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;

    setIsTranscribing(true);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = (reader.result as string).split(",")[1];
        
        const { data, error } = await supabase.functions.invoke("transcribe-audio", {
          body: { audio: base64Audio },
        });

        if (error) throw error;

        setTranscript(data.transcript || "Transcription not available");
        toast.success("Transcription complete");
        setIsTranscribing(false);
      };
    } catch (error) {
      console.error("Transcription error:", error);
      // Fallback mock transcript for demo
      setTranscript(
        "This is a simulated transcript of the sales call. " +
        "The customer discussed their needs for a new CRM solution. " +
        "They mentioned concerns about pricing and implementation timeline. " +
        "Follow-up action: Send proposal by end of week."
      );
      toast.info("Using demo transcript (transcription service not configured)");
      setIsTranscribing(false);
    }
  };

  const saveCall = async () => {
    if (!transcript && !audioBlob) {
      toast.error("Please record and transcribe a call first");
      return;
    }

    setIsSaving(true);

    try {
      const callData = {
        direction: "outbound" as const,
        status: "completed" as const,
        duration_seconds: recordingTime,
        transcript: transcript || undefined,
        notes: transcript ? `Auto-transcribed call (${formatTime(recordingTime)})` : undefined,
      };

      await createCall.mutateAsync(callData);
      
      toast.success("Call saved successfully");
      
      // Reset state
      setAudioBlob(null);
      setTranscript("");
      setRecordingTime(0);
    } catch (error) {
      toast.error("Failed to save call");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const discardRecording = () => {
    setAudioBlob(null);
    setTranscript("");
    setRecordingTime(0);
    toast.info("Recording discarded");
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-lg bg-chart-3/10">
          <Phone className="h-5 w-5 text-chart-3" />
        </div>
        <div>
          <h3 className="font-semibold">Voice Call Logger</h3>
          <p className="text-xs text-muted-foreground">Record, transcribe & analyze calls</p>
        </div>
      </div>

      {/* Recording Status */}
      <div className={cn(
        "p-4 rounded-lg text-center transition-all",
        isRecording ? "bg-destructive/10" : "bg-muted/30"
      )}>
        {isRecording ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2">
              <span className="h-3 w-3 rounded-full bg-destructive animate-pulse" />
              <span className="font-medium text-destructive">
                {isPaused ? "Paused" : "Recording..."}
              </span>
            </div>
            <p className="text-3xl font-mono font-bold">{formatTime(recordingTime)}</p>
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={togglePause}
              >
                {isPaused ? <Play className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={stopRecording}
              >
                <Square className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : audioBlob ? (
          <div className="space-y-3">
            <CheckCircle className="h-8 w-8 text-success mx-auto" />
            <p className="font-medium">Recording Complete</p>
            <p className="text-sm text-muted-foreground">Duration: {formatTime(recordingTime)}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Mic className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">Ready to record</p>
            <Button onClick={startRecording} className="gap-2">
              <Mic className="h-4 w-4" />
              Start Recording
            </Button>
          </div>
        )}
      </div>

      {/* Actions after recording */}
      {audioBlob && !isRecording && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={transcribeAudio}
              disabled={isTranscribing}
            >
              {isTranscribing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              {isTranscribing ? "Transcribing..." : "Transcribe"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={discardRecording}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {transcript && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Transcript</p>
              <div className="p-3 rounded-lg bg-muted/50 max-h-40 overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">{transcript}</p>
              </div>
            </div>
          )}

          <Button
            className="w-full gap-2"
            onClick={saveCall}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            {isSaving ? "Saving..." : "Save Call"}
          </Button>
        </div>
      )}
    </div>
  );
}
